import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Printer, Download, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import * as XLSX from 'xlsx';

interface AddressWorksheetProps {
  county: string;
  countyLabel: string;
}

type SortMode = 'date' | 'lastname';

const lastNameOf = (name: string): string => {
  const first = (name || '').split(';')[0].trim();
  if (!first) return '';
  if (first.includes(',')) return first.split(',')[0].trim().toUpperCase();
  const parts = first.split(/\s+/);
  return (parts[parts.length - 1] || '').toUpperCase();
};

export function AddressWorksheet({ county, countyLabel }: AddressWorksheetProps) {
  const [loadingMode, setLoadingMode] = useState<SortMode | null>(null);
  const { toast } = useToast();


  const downloadWorksheet = async (mode: SortMode) => {
    setLoadingMode(mode);
    try {
      const { data, error } = await supabase
        .from('county_research')
        .select('filing_date, grantor_name, grantor_address')
        .eq('county', county)
        .is('deleted_at', null);

      if (error) throw error;
      if (!data || data.length === 0) {
        toast({ title: 'No records', description: `No records found for ${countyLabel}`, variant: 'destructive' });
        return;
      }

      const needsAddress = data.filter(r => !r.grantor_address || r.grantor_address.trim() === '');
      if (needsAddress.length === 0) {
        toast({ title: 'All done', description: 'All records already have addresses!' });
        return;
      }

      const sorted = [...needsAddress].sort((a, b) => {
        if (mode === 'date') {
          return (a.filing_date || '').localeCompare(b.filing_date || '');
        }
        return lastNameOf(a.grantor_name || '').localeCompare(lastNameOf(b.grantor_name || ''));
      });

      const rows = sorted.map(r => ({
        'Filing Date': r.filing_date || '',
        'Seller Name': r.grantor_name || '',
        'Address (write in)': '',
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 14 }, { wch: 32 }, { wch: 70 }];
      ws['!rows'] = [{ hpt: 22 }];
      for (let i = 0; i < sorted.length; i++) ws['!rows'].push({ hpt: 42 });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, mode === 'date' ? 'By Filing Date' : 'By Last Name');

      const suffix = mode === 'date' ? 'by_FilingDate' : 'by_LastName';
      const filename = `${countyLabel.replace(/[^a-zA-Z0-9]/g, '_')}_AddressWorksheet_${suffix}.xlsx`;
      XLSX.writeFile(wb, filename);

      toast({ title: 'Worksheet downloaded', description: `${sorted.length} records → ${filename}` });
    } catch (err: any) {
      console.error('Worksheet error:', err);
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoadingMode(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Printer className="h-5 w-5" />
          Address Worksheet
        </CardTitle>
        <CardDescription>
          Download a 3-column printable worksheet for {countyLabel} (Filing Date, Seller Name, blank address column for handwriting). Pick the sort order.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-3">
        <Button onClick={() => downloadWorksheet('date')} disabled={loadingMode !== null} className="gap-2">
          {loadingMode === 'date' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Sorted by Filing Date
        </Button>
        <Button onClick={() => downloadWorksheet('lastname')} disabled={loadingMode !== null} variant="secondary" className="gap-2">
          {loadingMode === 'lastname' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Sorted by Seller Last Name
        </Button>
        <p className="text-xs text-muted-foreground w-full mt-1">
          Only records missing a seller address are included. Print landscape for best results.
        </p>
      </CardContent>
    </Card>
  );
}

